"use client";

import { wixSized } from "../../lib/media";
import { useState } from "react";
import {
  lineItemChoices,
  removeLineItem,
  setLineItemQuantity,
  type CartLineItem,
} from "../../lib/cart-store";

/**
 * The line-item list, shared by the cart drawer and the /cart page so the two
 * cannot drift apart.
 *
 * Quantity is a stepper rather than a fixed number: changing quantity used to
 * mean removing the line and re-adding it, which loses the shopper's size
 * choice. Wix clamps the requested quantity to remaining stock and returns the
 * clamped value, so what renders is always the server's answer.
 *
 * `busyId` is per line: one slow request must not freeze the controls on every
 * other row.
 */
export default function CartLines({
  items,
  onError,
  compact = false,
}: {
  items: CartLineItem[];
  onError: (message: string | null) => void;
  compact?: boolean;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  // line id -> quantity Wix could actually give us, when it clamped a request.
  const [clamped, setClamped] = useState<Record<string, number>>({});

  async function run(id: string, work: () => Promise<unknown>, failure: string) {
    setBusyId(id);
    onError(null);
    try {
      await work();
    } catch (err) {
      console.error("[cart]", failure, err);
      onError(failure);
    } finally {
      setBusyId(null);
    }
  }

  async function step(id: string, requested: number) {
    await run(
      id,
      async () => {
        const applied = await setLineItemQuantity(id, requested);
        setClamped((c) => {
          const next = { ...c };
          if (applied !== undefined && applied < requested) next[id] = applied;
          else delete next[id];
          return next;
        });
      },
      "Could not update quantity — please try again.",
    );
  }

  return (
    <ul className="divide-y divide-mist">
      {items.map((li) => {
        const id = li._id;
        const busy = Boolean(id) && busyId === id;
        const qty = li.quantity ?? 1;
        const choices = lineItemChoices(li);
        const atMax = Boolean(id) && clamped[id as string] !== undefined && qty >= clamped[id as string];
        return (
          <li key={id} className={`flex gap-4 ${compact ? "py-4" : "py-5"}`}>
            <div className={`${compact ? "h-20 w-16" : "h-24 w-20"} shrink-0 bg-panel`}>
              {li.image && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={wixSized(li.image, 160, 192)}
                  alt={li.productName?.original ?? ""}
                  className="h-full w-full object-cover"
                />
              )}
            </div>

            <div className="flex flex-1 flex-col justify-between gap-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm uppercase tracking-wide">
                    {li.productName?.original ?? "Item"}
                  </p>
                  {/* The shopper's size, straight off the Wix line item. Without
                      it a cart with two sizes of the same piece is two rows the
                      shopper cannot tell apart. */}
                  {choices.map((c) => (
                    <p key={c} className="mt-0.5 text-[12px] text-ink/70">
                      {c}
                    </p>
                  ))}
                </div>
                <p className="shrink-0 text-sm">{li.price?.formattedAmount ?? ""}</p>
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center border border-ink/20">
                  <button
                    type="button"
                    aria-label={`Decrease quantity of ${li.productName?.original ?? "item"}`}
                    disabled={busy || !id}
                    onClick={() => id && step(id, qty - 1)}
                    className="px-3 py-1.5 text-sm leading-none transition hover:bg-ink hover:text-paper disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ink"
                  >
                    −
                  </button>
                  <span className="min-w-8 px-2 text-center text-[13px] tabular-nums" aria-live="polite">
                    {busy ? "…" : qty}
                  </span>
                  <button
                    type="button"
                    aria-label={`Increase quantity of ${li.productName?.original ?? "item"}`}
                    disabled={busy || !id}
                    aria-disabled={atMax || undefined}
                    onClick={() => id && !atMax && step(id, qty + 1)}
                    className="px-3 py-1.5 text-sm leading-none transition hover:bg-ink hover:text-paper disabled:opacity-40 aria-disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ink"
                  >
                    +
                  </button>
                </div>

                <button
                  type="button"
                  disabled={busy || !id}
                  onClick={() =>
                    id && run(id, () => removeLineItem(id), "Could not remove item — please try again.")
                  }
                  className="min-h-6 py-1 text-[11px] uppercase tracking-wide text-ink/60 underline transition hover:text-ink disabled:opacity-40"
                >
                  Remove
                </button>
              </div>
              {/* Always mounted so the polite message is announced when it appears. */}
              <p role="status" className="text-[12px] text-ink/70 empty:-mt-2">
                {id && clamped[id] !== undefined ? `Only ${clamped[id]} available` : ""}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
